import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { BackButton } from '../common/styles';
import useIDPStoreMyGoals from '../../store/idpStoreMyGoals';
import useGridNavigation from '../../hooks/useGridNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { idpApi } from '../../services/api/idpApi';
import MyIDPFilters from './components/MyIDPFilters';
import IDPGrid from '../IndividualDevelopmentPlans/components/IDPGrid';
import IDPForm from '../IndividualDevelopmentPlans/components/IDPForm';
import {
  Container,
  Header,
  HeaderLeft,
  Title,
  Content,
  ControlsContainer,
  MainContainer,
  HeaderButton,
} from '../IndividualDevelopmentPlans/styles';

const MyIDPs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { user } = useAuth();
  const [showFilters, setShowFilters] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [goalStatuses, setGoalStatuses] = useState([]);
  const gridRef = useRef(null);

  const {
    goals,
    isLoading,
    isLoadingMore,
    pageInfo,
    fetchGoalsWithReset,
    fetchGoalsLoadMore,
    setIsReturningFromDetails,
    resetState,
    resetFilters,
    setSearchTerm,
    selectedStatuses,
    setSelectedStatuses,
  } = useIDPStoreMyGoals();

  // Fetch goal statuses for the filter
  useEffect(() => {
    const loadGoalStatuses = async () => {
      try {
        const statuses = await idpApi.getGoalStatuses();
        setGoalStatuses(statuses || []);
      } catch (err) {
        console.error('Failed to load goal statuses:', err);
      }
    };
    loadGoalStatuses();
  }, []);

  const loadMoreCallback = useCallback(() => {
    return fetchGoalsLoadMore();
  }, [fetchGoalsLoadMore]);

  // Callback to clean up filters when navigating away
  const handleNavigateAway = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Use the GridNavigation hook with My IDPs routes
  const {
    showLoadingSpinner,
    handleCombinedGridScroll,
    handleBackToItems,
    shouldHideContent,
    loadMoreData,
    shouldLoadData,
    shouldForceReload,
    isReturningFromDetailPage,
    hasItems,
    navigateWithScrollState,
  } = useGridNavigation({
    gridRef,
    items: goals,
    columnCount: 3,
    rowHeight: 230,
    routes: {
      list: '/my-idps',
      detail: (id) => `/my-idps/${id}`,
      additionalRoutes: [
        { path: '/view', shouldLoadData: false }
      ]
    },
    onReturnFromDetail: () => {
      setIsReturningFromDetails(true);
    },
    loadMoreDataCallback: loadMoreCallback,
    pageInfo,
    isLoadingMore,
    onNavigateAway: handleNavigateAway
  });

  // First effect: Always reset the store on mount
  useEffect(() => {
    resetState();
  }, [resetState]);

  // Second effect: Load data when needed
  useEffect(() => {
    // Only run this effect when we're on the main list view
    if (!shouldLoadData()) {
      return;
    }

    // Prevent reloading data if we already have items
    if (hasItems && !isLoadingMore && !shouldForceReload()) {
      return;
    }

    const forceReload = shouldForceReload();

    // Fetch data with reset parameter based on whether we need to force reload
    fetchGoalsWithReset(forceReload);

  }, [fetchGoalsWithReset, shouldLoadData, shouldForceReload, isReturningFromDetailPage, hasItems, isLoadingMore]);

  const handleBack = () => {
    navigate('/');
  };

  const handleSearchChange = (value) => {
    setSearchInputValue(value);
    setSearchTerm(value);
  };

  const isViewMode = location.pathname.includes('/view');

  const handleViewClick = (goal) => {
    navigateWithScrollState(goal, `/my-idps/${goal.id}/view`);
  };

  // Render form for existing IDP (always read-only in My IDPs)
  if (id) {
    return (
      <IDPForm
        goalId={id}
        onBack={handleBackToItems}
        readonly={true}
      />
    );
  }

  // Render list view
  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>My Development Plans</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '16px' }}>
          <HeaderButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <FaEyeSlash /> : <FaEye />}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </HeaderButton>
        </div>
      </Header>
      <Content>
        <ControlsContainer show={showFilters}>
          <MyIDPFilters
            searchInputValue={searchInputValue}
            setSearchInputValue={handleSearchChange}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            goalStatuses={goalStatuses}
          />
        </ControlsContainer>
        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0
          }}>
            <span style={{ fontWeight: 600 }}>
              {pageInfo?.totalCount || 0}
            </span>
            {pageInfo?.totalCount === 1 ? 'development plan' : 'development plans'} found
          </div>

          <IDPGrid
            goals={goals}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageInfo={pageInfo}
            handleItemClick={(goal) => handleViewClick(goal)}
            handleGridScroll={handleCombinedGridScroll}
            showLoadingSpinner={showLoadingSpinner}
            shouldHideContent={shouldHideContent}
            loadMoreData={loadMoreData}
            gridRef={gridRef}
            onView={handleViewClick}
            onEdit={handleViewClick}
            currentEmployeeId={user?.employee?.id}
          />
        </MainContainer>
      </Content>
    </Container>
  );
};

export default MyIDPs;
